/**
 * Mirrors server `therapistRecordingHasBlobPointersInDb` and
 * `isRecordingEligibleForAdminBulkTranscription` so admin UI can show
 * "Transcribe All Pending" only when the RPC would enqueue at least one row.
 *
 * Session note list uses $addFields that overwrites `uploadedAudioContentSlices`
 * with total binary size (number); treat number > 0 as "has slices".
 */

export const therapistRecordingHasBlobPointersInDbDisplay =
  (recording: {
    audioBlobContainerName?: string | null;
    audioBlobPaths?: unknown;
  }): boolean => {
    const container = recording.audioBlobContainerName;
    if (typeof container !== 'string' || container.trim() === '') {
      return false;
    }
    const paths = recording.audioBlobPaths;
    if (Array.isArray(paths)) {
      return paths.some(
        p => typeof p === 'string' && p.trim() !== '',
      );
    }
    if (typeof paths === 'string' && paths.trim() !== '') {
      try {
        const parsed = JSON.parse(paths) as unknown;
        if (Array.isArray(parsed)) {
          return parsed.some(
            p => typeof p === 'string' && String(p).trim() !== '',
          );
        }
      } catch {
        return true;
      }
    }
    return false;
  };

type RecordingLike = {
  status?: string;
  mediaType?: string | null;
  uploadedAudioContent?: unknown;
  uploadedAudioContentSlices?: unknown;
  audioBlobContainerName?: string | null;
  audioBlobPaths?: unknown;
};

const hasEmbeddableOrBlobAudioDisplay = (
  r: RecordingLike,
): boolean => {
  const isAudio =
    r.mediaType === 'audio' ||
    r.mediaType === undefined ||
    r.mediaType === null;
  if (!isAudio) {
    return true;
  }
  const slices = r.uploadedAudioContentSlices;
  const hasSliceData = Array.isArray(slices)
    ? slices.length > 0
    : typeof slices === 'number'
    ? slices > 0
    : false;
  if (hasSliceData || r.uploadedAudioContent) {
    return true;
  }
  return therapistRecordingHasBlobPointersInDbDisplay(r);
};

export const isRecordingEligibleForAdminBulkTranscriptionDisplay = (
  recording: RecordingLike,
): boolean => {
  const s = recording.status;
  if (s === 'uploaded' || s === 'transcribing' || s === 'failed') {
    return hasEmbeddableOrBlobAudioDisplay(recording);
  }
  if (s === 'transcribed') {
    return therapistRecordingHasBlobPointersInDbDisplay(recording);
  }
  return false;
};

export const hasAnyEligibleBulkTranscriptionRecording = (
  recordings: RecordingLike[] | undefined | null,
): boolean =>
  (recordings ?? []).some(
    isRecordingEligibleForAdminBulkTranscriptionDisplay,
  );
